import { useState } from 'react';
import type { PositionAnalysis, PositionAnalysisOption } from '@steward/shared';
import { cn } from '@/lib/utils';

const RISK_STYLES: Record<PositionAnalysis['riskLevel'], string> = {
  ok: 'bg-green-100 text-green-800',
  watch: 'bg-yellow-100 text-yellow-800',
  elevated: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const ACTION_LABELS: Record<PositionAnalysisOption['action'], string> = {
  hold: 'Hold',
  roll: 'Roll',
  close: 'Close',
  adjust: 'Adjust',
};

function OptionCard({ option }: { option: PositionAnalysisOption }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <button
      type="button"
      data-testid="analysis-option"
      onClick={() => setExpanded((v) => !v)}
      className="w-full rounded-lg border border-gray-200 p-3 text-left hover:border-gray-300"
    >
      <div className="flex items-center gap-2">
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold uppercase text-gray-700">
          {ACTION_LABELS[option.action]}
        </span>
        <span className="text-sm font-medium text-gray-900">{option.summary}</span>
      </div>
      {expanded && (
        <div className="mt-2 space-y-1 text-sm text-gray-600">
          <p><span className="font-medium text-gray-800">Why: </span>{option.rationale}</p>
          <p><span className="font-medium text-gray-800">Trade-offs: </span>{option.tradeoffs}</p>
        </div>
      )}
    </button>
  );
}

export function AnalysisCards({ analysis }: { analysis: PositionAnalysis }) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <span className={cn('mt-0.5 rounded-full px-2 py-0.5 text-xs font-semibold uppercase', RISK_STYLES[analysis.riskLevel])}>
          {analysis.riskLevel}
        </span>
        <p className="text-sm font-semibold text-gray-900">{analysis.headline}</p>
      </div>

      <p className="text-sm text-gray-700">{analysis.assessment}</p>

      {analysis.keyFactors.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
          {analysis.keyFactors.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}

      <div className="space-y-2">
        {analysis.options.map((o, i) => <OptionCard key={i} option={o} />)}
      </div>

      <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
        <span className="font-semibold">Suggested: </span>{analysis.suggestedAction}
      </div>

      {analysis.watchTriggers.length > 0 && (
        <div className="text-sm text-gray-600">
          <span className="font-medium text-gray-800">Watch for: </span>
          {analysis.watchTriggers.join(' · ')}
        </div>
      )}
    </div>
  );
}
